import React from "react";
import styled, { css } from "styled-components";
import * as _ from "lodash";

import Graph from "src/library/components/Graph";
import {
  OrderTotalByTypeResult,
  useWidgetDataApi
} from "src/core/dashboard/api/widget-data.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { GraphPayload } from "src/library/components/Graph/GraphToolTip";
import { OrderType } from "src/core/orders/api/orders.api";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { WidgetDisplayTypes } from "src/core/dashboard/api/dashboard.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import {
  getDisplayRange,
  getGraphMetrics,
  DisplayRangeType,
  getSelectedWidgetView,
  WidgetProps
} from "../utils";
import DataLoadError from "../DataLoadError";
import { groupOrderTotalsByType } from "./utils";
import { NumberWidget } from "../NumberWidget";
import { WidgetInnerHeader } from "../WidgetInnerHeader";
import { GraphDataType } from "../WidgetTotalValueSwitcher";
import { useWidgetFontSize } from "../hooks";

// The TS type for the flat object used to display data in graph
export interface OrderTotalByTypeGraphValue extends Omit<GraphPayload, "date">{
  /** xAxisBy date */
  date?: Date;
  adhoc?: number;
  bulk?: number;
  scheduled?: number;
  /** xAxisBy type */
  type?: OrderType;
  value?: number;
}

const ORDER_TOTALS_BY_TYPE_CACHE_KEY = "widget-order-totals-by-type";

/**
 * Returns order totals by type widget content
 */
export const OrderTotalsByTypeWidget: React.FunctionComponent<WidgetProps> = ({
  options, filters, widgetId, ignoreCache = false, widgetTypeId
}) => {
  const [ displayRange, setDisplayRange ] = React.useState<DisplayRangeType>("day");
  const [ ordersByTypeDataResponse, ordersByTypeDataRequest ] = useWidgetDataApi("GET_ORDER_TOTALS_BY_TYPE");
  const [ xAxisBy, setXAisBy ] = React.useState<"date" | "type">("type");
  const [ graphDataType, setGraphDataType ] = React.useState<GraphDataType>("value");
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { width } = useWidgetContainerSize(wrapperRef);
  const fontSize = useWidgetFontSize(width, options.view);

  // On load, get the data we need
  React.useEffect(() => {
    ordersByTypeDataRequest({
      widgetTypeId,
      filters,
      options: null
    }, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => `${ORDER_TOTALS_BY_TYPE_CACHE_KEY}-${widgetTypeId}`,
        ignoreCache
      }
    });
  }, [
    filters,
    ignoreCache,
    ordersByTypeDataRequest,
    widgetTypeId
  ]);

  const selectedView = React.useMemo(() => {
    return getSelectedWidgetView(options.view);
  }, [ options ]);

  // Takes the raw response from API and transforms the result to the shape required for graph data.
  // During this process, we also sets the displayRange & xAxisBy state
  const transformToGraphData = React.useCallback((orderTotalByTypeResult: OrderTotalByTypeResult): OrderTotalByTypeGraphValue[] => {
    // Find range to determine whether to display in days, weeks or months
    const calculatedDisplayRange = getDisplayRange(orderTotalByTypeResult);

    setDisplayRange(calculatedDisplayRange);
    setXAisBy(calculatedDisplayRange === "day" && selectedView !== "line" ? "type" : "date");

    // Group, accumulate totals and return
    return groupOrderTotalsByType(orderTotalByTypeResult, xAxisBy, calculatedDisplayRange, graphDataType);
  }, [ xAxisBy, graphDataType ]);

  // The data used to display graph metrics
  const graphDisplayData = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      const orderTotalByTypeResult = ordersByTypeDataResponse.data?.result;

      if (orderTotalByTypeResult) {
        return transformToGraphData(orderTotalByTypeResult);
      }
    }

    return null;
  }, [
    ordersByTypeDataResponse.data?.result,
    transformToGraphData,
    selectedView
  ]);

  const graphMetrics = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      return graphDisplayData ? getGraphMetrics(graphDisplayData, [ xAxisBy ]) : [];
    }

    return null;
  }, [
    graphDisplayData,
    xAxisBy,
    selectedView
  ]);

  // The data used to display number metrics
  const numberDisplayData = React.useMemo(() => {
    if (selectedView === WidgetDisplayTypes.Number) {
      const orderTotalByTypeResult = ordersByTypeDataResponse.data?.result;

      if (orderTotalByTypeResult) {
        const orderTotalsGroupedByType = groupOrderTotalsByType(orderTotalByTypeResult, "type") as Array<{type: string, value: number}>;

        return {
          value: orderTotalByTypeResult.reduce((value, order) => value + order.value, 0),
          total: orderTotalByTypeResult.reduce((total, order) => total + order.count, 0),
          data: orderTotalsGroupedByType.map(typeData => {
            return {
              group: _.startCase(typeData.type.replace(/[_-]/g, " ")),
              value: typeData.value,
              total: orderTotalByTypeResult.reduce((total, order) => {
                if (typeData.type === order.type) {
                  return total + order.count;
                }

                return total;
              }, 0)
            };
          })
        };
      }
    }

    return null;
  }, [ ordersByTypeDataResponse.data?.result, selectedView ]);

  if (selectedView !== "bar" && selectedView !== "line" && selectedView !== WidgetDisplayTypes.Number) {
    // Currently bar and line and number are the only handled view options availble below
    return null;
  }

  return (
    <StyledWrapper ref={wrapperRef}>
      {ordersByTypeDataResponse.pending && (
        <LoadingSpinner type="circular" />
      )}

      {graphDisplayData && graphMetrics && !ordersByTypeDataResponse.error && (selectedView === "line" || selectedView === "bar") && (
        <>
          <WidgetInnerHeader
            title="Order Totals"
            containerWidth={width}
            graphDataType={graphDataType}
            updateGraphDataType={setGraphDataType}
          />

          <Graph
            hideLegend={xAxisBy === "type"}
            containerWidth={width && width}
            stacked={xAxisBy === "date"}
            data={graphDisplayData}
            toolTipValueType={graphDataType === "value" ? "price" : "number"}
            type={selectedView}
            graphMetrics={graphMetrics}
            xAxis={{
              dataKey: xAxisBy,
              dateFormat: displayRange === "month" ? "month" : "day"
            }}
          />
        </>
      )}

      {numberDisplayData && fontSize && !ordersByTypeDataResponse.error && (
        <NumberWidget
          widgetId={widgetId}
          displayData={numberDisplayData}
          valueType="price"
          units="orders"
          fontSize={fontSize}
        />
      )}

      {ordersByTypeDataResponse.error && (
        <DataLoadError errorMessage={ordersByTypeDataResponse.error.message} />
      )}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    width: 100%;
    padding: ${theme.spacing.padding.sm} 0; 
  `}
`;
